import { useState, useEffect, useRef, useCallback } from 'react';
import { Phone, Users, ChevronLeft, ChevronDown, Pin, X, Trash2, UserCheck, Forward, Headphones, CheckCircle2, CheckSquare } from 'lucide-react';
import { Avatar } from '../../../components/Avatar';
import { useAuth } from '../../../lib/auth';
import { useSocket } from '../../../lib/socket';
import { useToast } from '../../../components/Toast';
import { useDialog } from '../../../components/DialogProvider';
import { formatHoraSP, parseSPDate, equipeRespondeSemSerDona, formatTelefoneBR } from '@fiodecobre/shared';
import { atendimentoApi } from '../lib/api';
import type { Conversa, Mensagem, EventoChamado, StatusMensagem } from '../lib/tipos';
import { MensagemBubble } from './MensagemBubble';
import { MensagemImagemGrupo } from './MensagemImagemGrupo';
import { BadgeEtiqueta } from './BadgeEtiqueta';
import { Composer } from './Composer';
import { PainelContato } from './PainelContato';
import { ModalEditarMensagem } from '../modais/ModalEditarMensagem';
import { ModalEncaminhar } from '../modais/ModalEncaminhar';

// Agrupa 3+ imagens consecutivas na mesma direcao em um unico bubble grid.
// Mensagens citadas/respostas nao agrupam (preservam contexto da resposta).
type ItemRender =
  | { tipo: 'msg'; mensagem: Mensagem }
  | { tipo: 'grupo-img'; mensagens: Mensagem[]; chave: string }
  | { tipo: 'evento'; evento: EventoChamado };

// Horario (ISO) de um item da timeline — pra ordenar e pra calcular separador de data.
function isoDoItem(item: ItemRender): string {
  if (item.tipo === 'msg') return item.mensagem.criado_em;
  if (item.tipo === 'grupo-img') return item.mensagens[0]!.criado_em;
  return item.evento.em;
}

// Timestamps sao wall-clock SP "sem TZ" e chegam em formatos diferentes (Z via socket,
// string crua via GET). new Date() interpreta os dois de jeitos diferentes (-3h num deles)
// e inverte a ordem de mensagens no mesmo minuto. parseSPDate normaliza ambos pro mesmo
// instante absoluto SP — usar SEMPRE pra ordenar/agrupar por data (ver shared/format/data.ts).
function msSP(iso: string): number {
  return parseSPDate(iso)?.getTime() ?? 0;
}

// Intercala os eventos de encaminhamento entre as mensagens. O evento aparece logo
// APOS as mensagens automaticas do sistema (outbound sem usuario_id — ex.: fly
// promocional + catalogo) que vem na sequencia do encaminhamento, e para na primeira
// mensagem do cliente/atendente. Assim o aviso fica depois do fly, nao antes.
// A HORA EXIBIDA continua a real (ev.em); muda so a POSICAO.
function intercalarEventos(itens: ItemRender[], eventos: EventoChamado[], mensagens: Mensagem[]): ItemRender[] {
  if (!eventos.length) return itens;
  const msgsOrd = [...mensagens].sort((a, b) => msSP(a.criado_em) - msSP(b.criado_em));
  function ordemEvento(em: string): number {
    const t0 = msSP(em);
    let ordem = t0;
    for (const m of msgsOrd) {
      const tm = msSP(m.criado_em);
      if (tm < t0) continue;
      const automatica = m.direcao === 'outbound' && m.usuario_id == null;
      if (automatica) { ordem = tm + 1; continue; } // empurra pra depois da automatica
      break; // primeira do cliente/atendente: para
    }
    return ordem;
  }
  const chave = (item: ItemRender): number =>
    item.tipo === 'evento' ? ordemEvento(item.evento.em) : msSP(isoDoItem(item));
  const eventoItens: ItemRender[] = eventos.map((e) => ({ tipo: 'evento' as const, evento: e }));
  return [...itens, ...eventoItens].sort((a, b) => chave(a) - chave(b));
}

function agruparImagens(mensagens: Mensagem[], ativo: boolean = true): ItemRender[] {
  if (!ativo) return mensagens.map((m) => ({ tipo: 'msg' as const, mensagem: m }));
  const itens: ItemRender[] = [];
  let i = 0;
  while (i < mensagens.length) {
    const m = mensagens[i]!;
    if (m.tipo === 'image' && !m.mensagem_citada_id) {
      let j = i + 1;
      while (
        j < mensagens.length &&
        mensagens[j]!.tipo === 'image' &&
        mensagens[j]!.direcao === m.direcao &&
        !mensagens[j]!.mensagem_citada_id &&
        // Nao agrupa atravessando a virada do dia: o separador de data usa a PRIMEIRA
        // mensagem do grupo e o bubble mostra a hora da ULTIMA, entao um grupo que cruza
        // a meia-noite prende a foto de hoje dentro do bloco de ontem — o "Hoje" so
        // aparecia na mensagem seguinte. (caso Eduardo Transportes, 24/07/2026)
        chaveData(mensagens[j]!.criado_em) === chaveData(m.criado_em)
      ) {
        j++;
      }
      const run = mensagens.slice(i, j);
      if (run.length >= 3) {
        itens.push({ tipo: 'grupo-img', mensagens: run, chave: `grupo-${run[0]!.id}` });
        i = j;
        continue;
      }
    }
    itens.push({ tipo: 'msg', mensagem: m });
    i++;
  }
  return itens;
}

function formatarRotuloData(iso: string): string {
  const d = parseSPDate(iso) ?? new Date(iso);
  const hoje = new Date(); hoje.setHours(0, 0, 0, 0);
  const ontem = new Date(hoje); ontem.setDate(hoje.getDate() - 1);
  const dia = new Date(d); dia.setHours(0, 0, 0, 0);
  if (dia.getTime() === hoje.getTime()) return 'Hoje';
  if (dia.getTime() === ontem.getTime()) return 'Ontem';
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' });
}

function chaveData(iso: string): string {
  const d = parseSPDate(iso) ?? new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function fileParaBase64(file: File | Blob): Promise<{ base64: string; mime: string; nome: string }> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const base64 = result.includes(',') ? result.split(',')[1]! : result;
      resolve({ base64, mime: file.type || 'application/octet-stream', nome: (file as File).name ?? 'arquivo.bin' });
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

interface Props {
  conversa: Conversa;
  onVoltar: () => void;
  onAtualizarConversa: (patch: Partial<Conversa> & { id: number }) => void;
  onApagarContato: (id: number) => void;
  // "Conversar" do cartao de contato (vCard) — abre a conversa com o numero do cartao.
  onConversarComNumero?: (telefone: string, nome: string) => void;
}

export function PainelChat({ conversa, onVoltar, onAtualizarConversa, onApagarContato, onConversarComNumero }: Props) {
  const { usuario } = useAuth();
  const { socket, connected } = useSocket();
  const toast = useToast();
  const dialog = useDialog();

  const PAGE_SIZE = 60;

  const [mensagens, setMensagens] = useState<Mensagem[]>([]);
  const [carregando, setCarregando] = useState(false);
  const [carregandoAntigas, setCarregandoAntigas] = useState(false);
  const [temMaisAntigas, setTemMaisAntigas] = useState(true);
  const [mostrarContato, setMostrarContato] = useState(false);
  const [responderA, setResponderA] = useState<Mensagem | null>(null);
  const [textoComposer, setTextoComposer] = useState<string | null>(null);
  const [editar, setEditar] = useState<Mensagem | null>(null);
  const [encaminharMsgs, setEncaminharMsgs] = useState<Mensagem[]>([]);
  const [arquivosArrastados, setArquivosArrastados] = useState<File[]>([]);
  const [arrastando, setArrastando] = useState(false);
  const [mostrarBtnDescer, setMostrarBtnDescer] = useState(false);
  // Etiqueta de data flutuante no topo (estilo WhatsApp): mostra a data do que esta
  // visivel enquanto rola e some sozinha quando para.
  const [dataFlutuante, setDataFlutuante] = useState<string | null>(null);
  const [dataFlutuanteVisivel, setDataFlutuanteVisivel] = useState(false);
  const [fixada, setFixada] = useState<Mensagem | null>(null);
  const [modoSelecao, setModoSelecao] = useState(false);
  const [selecionadas, setSelecionadas] = useState<Set<number>>(new Set());
  const [apagandoBatch, setApagandoBatch] = useState(false);
  const [encerrando, setEncerrando] = useState(false);
  // Painel de configuracoes da conversa (gear button no header).
  // Todos os clientes que usam este numero (vem do GET /api/conversas/:id).
  const [clientesNumero, setClientesNumero] = useState<Array<{ id: number; nome: string; codigo_legado: string | null }>>([]);
  // Eventos de encaminhamento do chamado — intercalados na timeline (DA-099 deixou silencioso; aqui voltam a aparecer).
  const [eventos, setEventos] = useState<EventoChamado[]>([]);

  const dragCounter = useRef(0);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const idAtualRef = useRef<number | null>(null);
  const inicialRef = useRef(false);
  // Ultima mensagem ja vista no fim da lista + se o usuario esta ancorado no rodape.
  // Os dois governam o auto-scroll (ver efeito abaixo).
  const ultimoIdRef = useRef<number | null>(null);
  const pertoDoFimRef = useRef(true);
  const ocultarDataRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const ct = conversa.contato;

  // Resolve os clientes vinculados ao numero (a lista de conversas nao traz isso).
  useEffect(() => {
    setClientesNumero([]);
    atendimentoApi.obterConversa(conversa.id)
      .then((c) => setClientesNumero(c.contato?.clientes ?? []))
      .catch(() => {});
  }, [conversa.id]);

  // Eventos de encaminhamento (do agente_selecao_log) pra mostrar na conversa.
  const carregarEventos = useCallback((id: number) => {
    atendimentoApi.listarEventosChamado(id).then(setEventos).catch(() => {});
  }, []);
  useEffect(() => {
    setEventos([]);
    carregarEventos(conversa.id);
  }, [conversa.id, carregarEventos]);

  const carregarInicial = useCallback(async () => {
    setCarregando(true);
    try {
      const lista = await atendimentoApi.listarMensagens(conversa.id, { limit: PAGE_SIZE });
      setMensagens(lista);
      setTemMaisAntigas(lista.length === PAGE_SIZE);
    } catch {
      toast.erro('Erro ao carregar mensagens');
    } finally {
      setCarregando(false);
    }
  }, [conversa.id]);

  const carregarAposAtual = useCallback(async (after: number) => {
    try {
      const lista = await atendimentoApi.listarMensagens(conversa.id, { after, limit: 200 });
      if (lista.length) setMensagens((p) => [...p, ...lista]);
    } catch {
      // silencioso — usuario ve via socket
    }
  }, [conversa.id]);

  // Paginacao reversa: scroll-up carrega anteriores. Mantem scroll anchor (escala
  // a posicao apos prepend pra nao saltar a tela do usuario).
  async function carregarAntigas() {
    const primeiraMsgId = mensagens[0]?.id;
    if (!primeiraMsgId || carregandoAntigas || !temMaisAntigas) return;
    const el = scrollRef.current;
    const alturaAntes = el?.scrollHeight ?? 0;
    setCarregandoAntigas(true);
    try {
      const antigas = await atendimentoApi.listarMensagens(conversa.id, { before: primeiraMsgId, limit: PAGE_SIZE });
      if (!antigas.length) {
        setTemMaisAntigas(false);
        return;
      }
      setMensagens((p) => [...antigas, ...p]);
      setTemMaisAntigas(antigas.length === PAGE_SIZE);
      // Restaura scroll position relativo ao crescimento
      requestAnimationFrame(() => {
        if (!el) return;
        const delta = el.scrollHeight - alturaAntes;
        el.scrollTop = el.scrollTop + delta;
      });
    } catch {
      toast.erro('Erro ao carregar mensagens anteriores');
    } finally {
      setCarregandoAntigas(false);
    }
  }

  useEffect(() => {
    if (idAtualRef.current === conversa.id) return;
    idAtualRef.current = conversa.id;
    inicialRef.current = true;
    ultimoIdRef.current = null;
    pertoDoFimRef.current = true;
    setMensagens([]);
    setTemMaisAntigas(true);
    setResponderA(null);
    setModoSelecao(false);
    setSelecionadas(new Set());
    carregarInicial();
    atendimentoApi.marcarLida(conversa.id).catch(() => {});
    if (!conversa.mensagem_fixada_id) setFixada(null);
  }, [conversa.id, carregarInicial]);

  // Identifica mensagem fixada (busca na lista carregada)
  useEffect(() => {
    if (!conversa.mensagem_fixada_id) { setFixada(null); return; }
    const m = mensagens.find((x) => x.id === conversa.mensagem_fixada_id);
    if (m) setFixada(m);
  }, [conversa.mensagem_fixada_id, mensagens]);

  // Auto-scroll pro fim SO quando chegou mensagem nova no FIM e o usuario ja estava
  // por la. Antes esse efeito descia a cada mudanca do array `mensagens` — e ele muda
  // muito mais do que "chegou mensagem": paginacao reversa (prepend das antigas),
  // recibo de entrega (✓✓), reacao, transcricao, edicao. Quem estava lendo o historico
  // era chutado pro rodape e tinha que rolar tudo de novo. Quem esta longe do fim ve o
  // botao ⌄ (mostrarBtnDescer) e desce quando quiser.
  useEffect(() => {
    if (!mensagens.length) { ultimoIdRef.current = null; return; }
    if (inicialRef.current) {
      ultimoIdRef.current = mensagens[mensagens.length - 1]!.id;
      if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      const t = setTimeout(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        inicialRef.current = false;
      }, 300);
      return () => clearTimeout(t);
    }
    const ultima = mensagens[mensagens.length - 1]!;
    const anexouNoFim = ultimoIdRef.current !== null && ultima.id !== ultimoIdRef.current;
    ultimoIdRef.current = ultima.id;
    if (!anexouNoFim) return;
    // Perto do fim: acompanha. Longe: so acompanha se a nova mensagem e um envio nosso
    // (quem acabou de mandar quer ver saindo), senao respeita onde o usuario esta.
    if (!pertoDoFimRef.current && ultima.direcao !== 'outbound') return;
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [mensagens]);

  useEffect(() => {
    if (!socket) return;
    const cid = conversa.id;

    function aoNovaMensagem(data: any) {
      // Backend emite em 2 formatos: { conversa_id, mensagem } OU { ...mensagem, conversa_id, source }
      const conversaIdEvt = data?.conversa_id;
      if (conversaIdEvt !== cid) return;
      const msg: Mensagem | undefined = data?.mensagem ?? (data?.id ? data : undefined);
      if (!msg?.id) return;
      setMensagens((p) => (p.some((x) => x.id === msg.id) ? p : [...p, msg]));
      atendimentoApi.marcarLida(cid).catch(() => {});
    }
    // Backend emite em 2 formatos, como o new_message: { mensagem } (rota HTTP) OU o
    // achatado do Baileys. Sem o `data?.mensagem?.id` defensivo, o formato achatado
    // estourava TypeError dentro do listener e a edicao do contato so aparecia no F5.
    function aoEditar(data: any) {
      const msg: Mensagem | undefined = data?.mensagem ?? (data?.id ? data : undefined);
      if (!msg?.id) return;
      setMensagens((p) => p.map((m) => (m.id === msg.id ? { ...m, ...msg } : m)));
    }
    function aoApagar(data: { id: number; conversa_id: number }) {
      if (data.conversa_id && data.conversa_id !== cid) return;
      setMensagens((p) => p.filter((m) => m.id !== data.id));
    }
    function aoConversaUpdate(data: any) {
      if (data?.id !== cid) return;
      if (data.mensagem_fixada_id !== undefined) {
        if (!data.mensagem_fixada_id) setFixada(null);
        // se setou, vai aparecer via useEffect dependente da lista
      }
      // Mudou atribuicao/status (encaminhamento, troca automatica) → recarrega os eventos
      // pra a nova linha aparecer na conversa ao vivo.
      if (data.usuario_atribuido_id !== undefined || data.status !== undefined) {
        atendimentoApi.listarEventosChamado(cid).then(setEventos).catch(() => {});
      }
    }
    // Status de entrega ao vivo (sent -> delivered -> read). O evento nao traz
    // conversa_id; casa pela id da mensagem (no-op se nao estiver na lista aberta).
    // Trava anti-rebaixamento: status atrasado nao desfaz um mais avancado
    // (ex.: 'delivered' atrasado nao apaga o 'read'). 'failed' sempre aplica.
    function aoStatus(data: { id: number; status: StatusMensagem }) {
      if (!data?.id || !data.status) return;
      const rank: Record<string, number> = { queued: 0, sent: 1, delivered: 2, read: 3 };
      setMensagens((p) => p.map((m) => {
        if (m.id !== data.id) return m;
        if (data.status !== 'failed' && (rank[data.status] ?? -1) <= (rank[m.status] ?? -1)) return m;
        return { ...m, status: data.status };
      }));
    }
    function aoReacao(data: { id: number; conversa_id: number; reactions: any[] }) {
      if (data.conversa_id !== cid) return;
      setMensagens((p) => p.map((m) => m.id === data.id
        ? { ...m, metadata: { ...(m.metadata ?? {}), reactions: data.reactions } }
        : m,
      ));
    }
    function aoTranscricao(data: { id: number; conversa_id: number; transcricao: { texto: string; modelo: string; em: string } }) {
      if (data.conversa_id !== cid) return;
      setMensagens((p) => p.map((m) => m.id === data.id
        ? { ...m, metadata: { ...(m.metadata ?? {}), transcricao: data.transcricao } }
        : m,
      ));
    }

    socket.on('new_message', aoNovaMensagem);
    socket.on('message_updated', aoEditar);
    socket.on('message_deleted', aoApagar);
    socket.on('conversation_updated', aoConversaUpdate);
    socket.on('message_reaction', aoReacao);
    socket.on('message_transcribed', aoTranscricao);
    socket.on('message_status', aoStatus);
    return () => {
      socket.off('new_message', aoNovaMensagem);
      socket.off('message_updated', aoEditar);
      socket.off('message_deleted', aoApagar);
      socket.off('conversation_updated', aoConversaUpdate);
      socket.off('message_reaction', aoReacao);
      socket.off('message_transcribed', aoTranscricao);
      socket.off('message_status', aoStatus);
    };
  }, [socket, conversa.id]);

  // Recarregar ao reconectar — pega so msgs novas via after=ultimoId
  const prevConn = useRef(connected);
  useEffect(() => {
    if (connected && !prevConn.current) {
      const ult = mensagens[mensagens.length - 1]?.id;
      if (ult) carregarAposAtual(ult);
      else carregarInicial();
    }
    prevConn.current = connected;
  }, [connected, carregarInicial, carregarAposAtual, mensagens]);

  // Limpa o timer da etiqueta de data ao trocar de conversa / desmontar.
  useEffect(() => {
    setDataFlutuanteVisivel(false);
    return () => { if (ocultarDataRef.current) clearTimeout(ocultarDataRef.current); };
  }, [conversa.id]);

  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const dist = el.scrollHeight - el.scrollTop - el.clientHeight;
    setMostrarBtnDescer(dist > 300);
    pertoDoFimRef.current = dist < 150;
    // Data flutuante: pega a data do ultimo item cujo topo ja passou pra cima do viewport
    // (= o que esta no topo da area visivel) e mostra a etiqueta, somindo apos parar de rolar.
    const contTop = el.getBoundingClientRect().top;
    const marcados = el.querySelectorAll<HTMLElement>('[data-iso]');
    let isoVisivel: string | null = null;
    for (const node of marcados) {
      if (node.getBoundingClientRect().top <= contTop + 12) isoVisivel = node.dataset.iso ?? null;
      else break;
    }
    if (isoVisivel) {
      setDataFlutuante(formatarRotuloData(isoVisivel));
      setDataFlutuanteVisivel(true);
      if (ocultarDataRef.current) clearTimeout(ocultarDataRef.current);
      ocultarDataRef.current = setTimeout(() => setDataFlutuanteVisivel(false), 1500);
    }
    if (inicialRef.current) return;
    // Scroll perto do topo dispara paginacao reversa (debounce natural pelo
    // setCarregandoAntigas que evita reentry).
    if (el.scrollTop < 200 && mensagens.length && temMaisAntigas && !carregandoAntigas) {
      carregarAntigas();
    }
  };

  // Janela de 24h fechada (backend devolve 409 com janela_fechada) -> popup explicativo
  // em vez de toast generico. Outros erros seguem no toast.
  function tratarErroEnvio(e: any, fallback: string) {
    if (e?.corpo?.janela_fechada) {
      void dialog.alerta(e?.message ?? fallback, { titulo: 'Nao foi possivel enviar', tipo: 'aviso' });
      return;
    }
    toast.erro(e?.message ?? fallback);
  }

  async function enviarTexto(texto: string) {
    try {
      await atendimentoApi.enviarTexto(conversa.id, { texto, mensagem_citada_id: responderA?.id ?? null });
      setResponderA(null);
    } catch (e: any) {
      tratarErroEnvio(e, 'Erro ao enviar mensagem');
    }
  }

  async function enviarMidia(file: File, legenda?: string): Promise<boolean> {
    try {
      const { base64, mime, nome } = await fileParaBase64(file);
      const tipo = file.type.startsWith('image/') ? 'image' : file.type.startsWith('video/') ? 'video' : 'document';
      await atendimentoApi.enviarMidia(conversa.id, { tipo, base64, mime, nome, texto: legenda, mensagem_citada_id: responderA?.id ?? null });
      setResponderA(null);
      return true;
    } catch (e: any) {
      tratarErroEnvio(e, 'Erro ao enviar arquivo');
      return false;
    }
  }

  async function enviarPix() {
    const nome = ct.nome || ct.push_name || ct.telefone || 'este contato';
    const ok = await dialog.confirmar(`Enviar o Pix da loja (QR Code + CNPJ) para ${nome}?`, {
      titulo: 'Enviar Pix',
      textoConfirmar: 'Enviar Pix',
    });
    if (!ok) return;
    try {
      const r = await atendimentoApi.enviarPix(conversa.id);
      if (r.aviso) await dialog.alerta(r.aviso);
      else toast.sucesso('Pix enviado');
    } catch (e: any) {
      tratarErroEnvio(e, 'Erro ao enviar Pix');
    }
  }

  async function enviarAudio(blob: Blob) {
    try {
      const { base64, mime } = await fileParaBase64(blob);
      await atendimentoApi.enviarAudio(conversa.id, { base64, mime, mensagem_citada_id: responderA?.id ?? null });
      toast.sucesso('Audio enviado');
      setResponderA(null);
    } catch (e: any) {
      tratarErroEnvio(e, 'Erro ao enviar audio');
    }
  }

  async function transcrever(m: Mensagem) {
    try {
      const r = await atendimentoApi.transcreverMensagem(m.id);
      setMensagens((p) => p.map((x) => x.id === m.id
        ? { ...x, metadata: { ...(x.metadata ?? {}), transcricao: { texto: r.texto, modelo: r.modelo, em: r.em } } }
        : x,
      ));
    } catch (e: any) {
      toast.erro(e?.message ?? 'Erro ao transcrever audio');
    }
  }

  async function reagir(m: Mensagem, emoji: string | null) {
    try {
      await atendimentoApi.reagirMensagem(m.id, emoji);
      // Update otimista (em paralelo, socket vai chegar logo depois com versao final)
      setMensagens((p) => p.map((x) => x.id === m.id
        ? { ...x, metadata: {
            ...(x.metadata ?? {}),
            reactions: [
              ...(((x.metadata?.reactions as any[]) ?? []) as any[]).filter((r) => !r.fromMe),
              ...(emoji ? [{ emoji, jid: 'loja', name: 'Voce', fromMe: true, at: new Date().toISOString() }] : []),
            ],
          } }
        : x,
      ));
    } catch (e: any) {
      toast.erro(e?.message ?? 'Erro ao reagir');
    }
  }

  async function aoEditarSalvar(novoTexto: string) {
    if (!editar) return;
    try {
      await atendimentoApi.editarMensagem(editar.id, novoTexto);
      setEditar(null);
      toast.sucesso('Mensagem editada');
    } catch (e: any) {
      toast.erro(e?.message ?? 'Erro ao editar');
    }
  }

  async function pedirApagar(m: Mensagem) {
    const ok = await dialog.confirmar('A mensagem sera apagada apenas no sistema.', {
      titulo: 'Apagar mensagem?',
      textoConfirmar: 'Apagar',
      perigoso: true,
    });
    if (!ok) return;
    try {
      await atendimentoApi.excluirMensagem(m.id);
      toast.sucesso('Mensagem apagada');
    } catch {
      toast.erro('Erro ao apagar');
    }
  }

  async function pedirApagarParaTodos(m: Mensagem) {
    const ok = await dialog.confirmar('A mensagem sera apagada aqui E no WhatsApp do contato.', {
      titulo: 'Apagar para todos?',
      textoConfirmar: 'Apagar para todos',
      perigoso: true,
    });
    if (!ok) return;
    try {
      const r = await atendimentoApi.excluirMensagem(m.id, { paraTodos: true });
      if (r.remoto === 'ok') toast.sucesso('Apagada aqui e no WhatsApp do contato');
      else toast.aviso('Apagada aqui, mas nao foi possivel apagar no WhatsApp do contato');
    } catch {
      toast.erro('Erro ao apagar');
    }
  }

  async function aoFixar(m: Mensagem) {
    try {
      await atendimentoApi.atualizarConversa(conversa.id, { mensagem_fixada_id: m.id });
      onAtualizarConversa({ id: conversa.id, mensagem_fixada_id: m.id });
      setFixada(m);
      toast.sucesso('Mensagem fixada');
    } catch {
      toast.erro('Erro ao fixar');
    }
  }
  async function aoDesfixar() {
    try {
      await atendimentoApi.atualizarConversa(conversa.id, { mensagem_fixada_id: null });
      onAtualizarConversa({ id: conversa.id, mensagem_fixada_id: null });
      setFixada(null);
      toast.sucesso('Mensagem desafixada');
    } catch {
      toast.erro('Erro ao desafixar');
    }
  }

  // Encerrar atendimento (1 clique). Reabre sozinho se o cliente voltar a escrever (DA-099).
  // Backend credita "atendido" pra dona (DA-105/RDS 1.16). Onda Encerramento de Chamado, Fase 1.
  async function aoEncerrar() {
    if (encerrando) return;
    setEncerrando(true);
    try {
      await atendimentoApi.atualizarStatusChamado(conversa.id, 'encerrado');
      onAtualizarConversa({ id: conversa.id, status: 'encerrado' });
      toast.sucesso('Atendimento encerrado');
    } catch (e: any) {
      toast.erro(e?.message ?? 'Erro ao encerrar');
    } finally {
      setEncerrando(false);
    }
  }

  // ESC: prioridade modo selecao > fechar conversa.
  useEffect(() => {
    const k = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (modoSelecao) { sairSelecao(); return; }
      if (!encaminharMsgs.length && !editar && !mostrarContato) onVoltar();
    };
    document.addEventListener('keydown', k);
    return () => document.removeEventListener('keydown', k);
  }, [encaminharMsgs, editar, mostrarContato, onVoltar, modoSelecao]);

  const onDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setArrastando(false);
    dragCounter.current = 0;
    const fs = e.dataTransfer?.files;
    if (fs && fs.length) setArquivosArrastados(Array.from(fs));
  }, []);
  const onDragOver = useCallback((e: React.DragEvent) => e.preventDefault(), []);
  const onDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    dragCounter.current++;
    if (e.dataTransfer?.types?.includes('Files')) setArrastando(true);
  }, []);
  const onDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    dragCounter.current--;
    if (dragCounter.current <= 0) { setArrastando(false); dragCounter.current = 0; }
  }, []);

  const mensagemPorId = new Map(mensagens.map((m) => [m.id, m]));
  const itensRender = intercalarEventos(agruparImagens(mensagens, !modoSelecao), eventos, mensagens);

  function entrarSelecao(m: Mensagem) {
    setModoSelecao(true);
    setSelecionadas(new Set([m.id]));
  }
  function entrarSelecaoGrupo(ms: Mensagem[]) {
    setModoSelecao(true);
    setSelecionadas(new Set(ms.map((m) => m.id)));
  }
  function sairSelecao() {
    setModoSelecao(false);
    setSelecionadas(new Set());
  }
  function toggleSelecao(m: Mensagem) {
    setSelecionadas((prev) => {
      const novo = new Set(prev);
      if (novo.has(m.id)) novo.delete(m.id); else novo.add(m.id);
      return novo;
    });
  }
  function encaminharSelecionadas() {
    // Mantem a ordem do chat (anexos vao na sequencia em que aparecem).
    const msgs = mensagens.filter((m) => selecionadas.has(m.id));
    if (!msgs.length) return;
    setEncaminharMsgs(msgs);
    sairSelecao();
  }
  async function apagarSelecionadas() {
    const ids = Array.from(selecionadas);
    if (!ids.length) return;
    const ok = await dialog.confirmar(
      `${ids.length} ${ids.length === 1 ? 'mensagem sera apagada' : 'mensagens serao apagadas'} apenas no sistema.`,
      { titulo: `Apagar ${ids.length} ${ids.length === 1 ? 'mensagem' : 'mensagens'}?`, textoConfirmar: 'Apagar', perigoso: true },
    );
    if (!ok) return;
    setApagandoBatch(true);
    try {
      const resultados = await Promise.allSettled(ids.map((id) => atendimentoApi.excluirMensagem(id)));
      const falhas = resultados.filter((r) => r.status === 'rejected').length;
      if (falhas === 0) toast.sucesso(`${ids.length} ${ids.length === 1 ? 'mensagem apagada' : 'mensagens apagadas'}`);
      else if (falhas < ids.length) toast.erro(`${ids.length - falhas} apagadas, ${falhas} falharam`);
      else toast.erro('Erro ao apagar mensagens');
      sairSelecao();
    } finally {
      setApagandoBatch(false);
    }
  }

  return (
    <div className="flex-1 flex">
      <div className="flex-1 flex flex-col min-w-0 relative" onDrop={onDrop} onDragOver={onDragOver} onDragEnter={onDragEnter} onDragLeave={onDragLeave}>
        {arrastando && (
          <div className="absolute inset-0 z-50 bg-blue-50/90 border-2 border-dashed border-blue-400 rounded-lg flex items-center justify-center pointer-events-none">
            <div className="flex flex-col items-center gap-2 text-blue-600">
              <span className="text-sm font-medium">Solte o arquivo aqui</span>
            </div>
          </div>
        )}

        {modoSelecao ? (
          <div className="min-h-[3.5rem] flex items-center px-3 gap-3 flex-shrink-0" style={{ background: 'var(--wa-chat-header-bg, #252526)', borderBottom: '1px solid rgba(255,255,255,0.12)' }}>
            <button
              onClick={sairSelecao}
              className="p-1 rounded hover:bg-white/10"
              style={{ color: 'var(--wa-text-fg, #e5e7eb)' }}
              title="Sair da selecao (Esc)"
            >
              <X size={22} />
            </button>
            <p className="font-medium text-sm flex-1" style={{ color: 'var(--wa-text-fg, #e5e7eb)' }}>
              {selecionadas.size === 0
                ? 'Selecione mensagens'
                : `${selecionadas.size} ${selecionadas.size === 1 ? 'selecionada' : 'selecionadas'}`}
            </p>
            <button
              onClick={encaminharSelecionadas}
              disabled={selecionadas.size === 0}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:bg-gray-500 disabled:cursor-not-allowed"
            >
              <Forward size={14} />
              Encaminhar
            </button>
            <button
              onClick={apagarSelecionadas}
              disabled={selecionadas.size === 0 || apagandoBatch}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded text-sm font-medium bg-red-500 text-white hover:bg-red-600 disabled:bg-gray-500 disabled:cursor-not-allowed"
            >
              <Trash2 size={14} />
              Apagar
            </button>
          </div>
        ) : (
        <div className="min-h-[3.5rem] flex items-center px-3 gap-3 flex-shrink-0" style={{ background: 'var(--wa-chat-header-bg, #252526)', borderBottom: '1px solid rgba(255,255,255,0.12)' }}>
          <button onClick={onVoltar} className="md:hidden p-1" style={{ color: 'var(--wa-subtext-fg, #9ca3af)' }}>
            <ChevronLeft size={20} />
          </button>
          {ct.grupo && !ct.foto_url ? (
            <div className="w-9 h-9 rounded-full bg-green-600 flex items-center justify-center flex-shrink-0 cursor-pointer" onClick={() => setMostrarContato(!mostrarContato)}>
              <Users size={18} className="text-white" />
            </div>
          ) : (
            <div className="flex-shrink-0 cursor-pointer" onClick={() => setMostrarContato(!mostrarContato)}>
              <Avatar nome={ct.nome || ct.push_name || ''} fotoUrl={ct.foto_url} tamanho={36} />
            </div>
          )}
          <div className="cursor-pointer" style={{ flex: '1 1 0%', minWidth: 0 }} onClick={() => setMostrarContato(!mostrarContato)}>
            <p className="font-medium text-sm truncate" style={{ color: 'var(--wa-text-fg, #e5e7eb)' }}>
              {[ct.nome, ct.sobrenome].filter(Boolean).join(' ')
                || ct.push_name
                || ct.telefone
                || 'Desconhecido'}
            </p>
            {(() => {
              // Mostra TODOS os clientes que usam este numero (numero compartilhado
              // por mais de uma empresa, ex. grupo THRU). Fallback: o vinculo unico.
              const lista = clientesNumero.length
                ? clientesNumero
                : ct.clientes?.length
                  ? ct.clientes
                  : ct.cliente_nome
                    ? [{ id: ct.cliente_id ?? 0, nome: ct.cliente_nome, codigo_legado: ct.cliente_codigo ?? null }]
                    : [];
              if (!lista.length) return null;
              return (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 2 }}>
                  {lista.map((cli) => (
                    <span
                      key={cli.id}
                      title={cli.codigo_legado ? `Cliente #${cli.codigo_legado}` : 'Cliente vinculado'}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 4,
                        background: 'var(--accent)',
                        color: 'var(--accent-fg)',
                        fontSize: 11,
                        fontWeight: 700,
                        padding: '1px 7px',
                        borderRadius: 4,
                        maxWidth: '100%',
                      }}
                    >
                      <UserCheck size={11} style={{ flexShrink: 0 }} />
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {cli.nome}{cli.codigo_legado ? ` · #${cli.codigo_legado}` : ''}
                      </span>
                    </span>
                  ))}
                </div>
              );
            })()}
            <div className="flex items-center gap-1.5 flex-wrap">
              {/* Quem esta atendendo este chamado — so pra admin/gerente (a vendedora ja sabe
                  pelo banner/Composer). Informativo, nao trava nada. Mesmo azul do badge da lista. */}
              {(usuario?.perfil === 'admin' || usuario?.perfil === 'gerente')
                && conversa.inbox === 'atendimento'
                && conversa.usuario_atribuido_id != null && (
                <span
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-bold leading-none bg-blue-600 text-white"
                  title={`Chamado atribuido a ${conversa.usuario_atribuido_login || conversa.usuario_atribuido_nome || 'atendente'}`}
                >
                  <Headphones size={11} style={{ flexShrink: 0 }} />
                  Atendendo: {(conversa.usuario_atribuido_login || conversa.usuario_atribuido_nome)?.split(' ')[0] ?? '—'}
                  {eventos.length > 0 && (
                    <span style={{ opacity: 0.85 }}> · {formatHoraSP(eventos[eventos.length - 1]!.em)}</span>
                  )}
                </span>
              )}
              {ct.grupo && <span className="text-xs" style={{ color: 'var(--wa-subtext-fg, #9ca3af)' }}>Grupo</span>}
              {!ct.grupo && (ct.telefone || ct.jid) && (
                <span className="flex items-center gap-0.5 text-xs" style={{ color: 'var(--wa-subtext-fg, #9ca3af)' }}>
                  <Phone size={10} />
                  {/* ⚠️ Pela PECA, nunca cru: desde a [DA-708] a coluna guarda so' digitos, e
                      imprimir o dado direto sairia `556492326324` colado. */}
                  {/* Trocar o numero mora na ficha (Editar contato), que confere com o WhatsApp —
                      o atalho "corrigir" que nasceu aqui em 24/09/2026 saiu no mesmo dia, a pedido
                      dele, pra nao haver duas portas pro mesmo dado. */}
                  {formatTelefoneBR(ct.telefone || ct.jid?.replace(/@.*/, ''))}
                </span>
              )}
              {(ct.etiquetas ?? []).filter((e) => !e.ocultar).map((e) => (
                <BadgeEtiqueta key={e.id} nome={e.nome} cor={e.cor} apelido={e.apelido} />
              ))}
            </div>
          </div>

          {/* Porta VISIVEL do modo selecao (antes so existia no menu ⋮ de uma mensagem, e
              ninguem descobria que dali dava pra marcar VARIAS e encaminhar de uma vez). */}
          {mensagens.length > 0 && (
            <button
              onClick={() => setModoSelecao(true)}
              title="Selecionar mensagens para encaminhar ou apagar"
              className="flex-shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition bg-blue-600 text-white hover:bg-blue-700"
            >
              <CheckSquare size={15} /> Selecionar
            </button>
          )}

{/* Encerrar atendimento (Onda Encerramento de Chamado, Fase 1). 1 clique. Reabre sozinho
              se o contato voltar a escrever (DA-099, vale nas 2 caixas). Quem pode encerrar:
              admin/gerente (qualquer caixa, inclusive Compras) ou a dona no atendimento. */}
          {(() => {
            const ehAdmin = usuario?.perfil === 'admin' || usuario?.perfil === 'gerente';
            const minha = conversa.usuario_atribuido_id === usuario?.id;
            const podeEncerrar = ehAdmin || (conversa.inbox === 'atendimento' && minha);
            if (!podeEncerrar) return null;
            if (conversa.status === 'encerrado') {
              return (
                <span
                  className="flex-shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] font-bold leading-none"
                  style={{ background: '#e5e7eb', color: '#4b5563' }}
                  title="Encerrado — reabre sozinho se o contato voltar a escrever"
                >
                  <CheckCircle2 size={13} /> Encerrado
                </span>
              );
            }
            const rotulo = conversa.inbox === 'atendimento' ? 'Encerrar atendimento' : 'Encerrar conversa';
            return (
              <button
                onClick={aoEncerrar}
                disabled={encerrando}
                title="Encerrar (reabre sozinho se o contato voltar a escrever)"
                className="flex-shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition disabled:opacity-50"
                style={{ background: '#fde68a', color: '#000' }}
              >
                <CheckCircle2 size={15} /> {encerrando ? 'Encerrando...' : rotulo}
              </button>
            );
          })()}
        </div>
        )}

        {fixada && (
          <div className="flex items-center gap-2 px-4 py-2 bg-amber-50 border-b border-amber-200 flex-shrink-0 cursor-pointer hover:bg-amber-100"
            onClick={() => {
              const el = document.getElementById(`msg-${fixada.id}`);
              if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                el.classList.add('ring-2', 'ring-amber-400');
                setTimeout(() => el.classList.remove('ring-2', 'ring-amber-400'), 2000);
              }
            }}
          >
            <Pin size={14} className="text-amber-600 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-amber-700">Mensagem fixada</p>
              <p className="text-xs text-gray-600 truncate">
                {fixada.tipo === 'image' ? '[Imagem]' : fixada.tipo === 'audio' ? '[Audio]' : (fixada.texto ?? '').slice(0, 80)}
              </p>
            </div>
            <button onClick={(e) => { e.stopPropagation(); aoDesfixar(); }} className="text-gray-400 hover:text-gray-600 flex-shrink-0">
              <X size={14} />
            </button>
          </div>
        )}

        <div className="flex-1 flex flex-col min-h-0 relative">
        {/* Etiqueta de data flutuante no topo (estilo WhatsApp) — overlay, nao rola junto */}
        {dataFlutuante && (
          <div className="absolute top-2 left-0 right-0 flex justify-center pointer-events-none z-20"
            style={{ opacity: dataFlutuanteVisivel ? 1 : 0, transition: 'opacity 200ms' }}>
            <span className="px-3 py-1 bg-white rounded-lg shadow text-xs text-gray-600 font-medium">
              {dataFlutuante}
            </span>
          </div>
        )}
        <div ref={scrollRef} className="flex-1 overflow-y-auto overflow-x-hidden px-2 md:px-4 py-3" style={{ background: 'var(--wa-chat-bg, #b3b3b3)' }} onScroll={onScroll}>
          {carregando && mensagens.length === 0 && <div className="text-center text-gray-400 py-4">Carregando...</div>}
          {carregandoAntigas && (
            <div className="text-center py-2 text-gray-500 text-xs flex items-center justify-center gap-1.5">
              <span className="inline-block w-3 h-3 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
              Carregando anteriores...
            </div>
          )}
          {!temMaisAntigas && mensagens.length > 0 && !carregandoAntigas && (
            <div className="text-center py-2"><span className="text-gray-400 text-xs">Inicio da conversa</span></div>
          )}
          {itensRender.map((item, i) => {
            const isoAtual = isoDoItem(item);
            const itemAnt = i > 0 ? itensRender[i - 1]! : null;
            const isoAnt = itemAnt ? isoDoItem(itemAnt) : null;
            const mostrarSep = chaveData(isoAtual) !== (isoAnt ? chaveData(isoAnt) : null);
            const sep = mostrarSep && (
              <div className="flex justify-center my-3">
                <span className="px-3 py-1 bg-white rounded-lg shadow-sm text-xs text-gray-500 font-medium">
                  {formatarRotuloData(isoAtual)}
                </span>
              </div>
            );
            if (item.tipo === 'evento') {
              const ev = item.evento;
              const acao = ev.assumido
                ? `Chamado assumido por ${ev.agente_nome ?? 'administracao'}`
                : ev.por === 'Sistema'
                  ? `Chamado encaminhado para ${ev.agente_nome ?? 'atendente'}`
                  : `Chamado encaminhado para ${ev.agente_nome ?? 'atendente'} por ${ev.por}`;
              // Renderiza como mensagem normal: balao a esquerda (bubble-in), fonte das
              // mensagens (16px), com a assinatura "Atendimento FIODECOBRE" igual aos envios do sistema.
              return (
                <div key={`evt-${ev.id}`} data-iso={isoAtual}>
                  {sep}
                  <div className="group flex mb-1.5 justify-start">
                    <div className="relative" style={{ maxWidth: 'min(85%, 520px)', width: 'fit-content', marginLeft: 40 }}>
                      <div className="bubble-in">
                        <div className="flex items-center gap-1.5 mb-0.5">
                          <span className="font-bold text-gray-700" style={{ fontSize: '19.2px' }}>Atendimento FIODECOBRE</span>
                        </div>
                        <div className="flex items-start gap-1.5" style={{ fontSize: '16px' }}>
                          <Forward size={16} className="text-gray-500 flex-shrink-0 mt-0.5" />
                          <span className="break-words">{acao}</span>
                        </div>
                        <div className="flex items-center gap-1 mt-0.5 justify-start">
                          <span className="text-[10px] text-gray-400">{formatHoraSP(ev.em)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            }
            if (item.tipo === 'grupo-img') {
              return (
                <div key={item.chave} data-iso={isoAtual}>
                  {sep}
                  <MensagemImagemGrupo
                    mensagens={item.mensagens}
                    contato={ct}
                    onSelecionarTodas={entrarSelecaoGrupo}
                  />
                </div>
              );
            }
            const m = item.mensagem;
            const citada = m.mensagem_citada_id ? mensagemPorId.get(m.mensagem_citada_id) ?? null : null;
            return (
              <div key={m.id} data-iso={isoAtual}>
                {sep}
                <MensagemBubble
                  msg={m}
                  msgCitada={citada}
                  contato={ct}
                  inbox={conversa.inbox}
                  onResponder={() => setResponderA(m)}
                  onEncaminhar={() => setEncaminharMsgs([m])}
                  onEditar={() => setEditar(m)}
                  onApagar={() => void pedirApagar(m)}
                  onApagarParaTodos={() => void pedirApagarParaTodos(m)}
                  onFixar={() => aoFixar(m)}
                  onReagir={(_, emoji) => void reagir(m, emoji)}
                  onTranscrever={(x) => transcrever(x)}
                  onUsarComoMensagem={(texto) => setTextoComposer(texto)}
                  onConversarComNumero={onConversarComNumero}
                  onIniciarSelecao={() => entrarSelecao(m)}
                  modoSelecao={modoSelecao}
                  selecionada={selecionadas.has(m.id)}
                  onToggleSelecao={() => toggleSelecao(m)}
                />
              </div>
            );
          })}
          <div ref={bottomRef} />
        </div>
        </div>

        {mostrarBtnDescer && (
          <button
            onClick={() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' })}
            className="absolute bottom-20 right-6 z-30 w-10 h-10 rounded-full bg-white shadow-lg border border-gray-200 flex items-center justify-center text-gray-500 hover:text-gray-700 hover:shadow-xl transition-all"
          >
            <ChevronDown size={22} />
          </button>
        )}

        {(() => {
          // Bloqueia interacao conforme DA-027 (atribuicao restrita em atendimento):
          //  - admin/gerente: sempre libera. Em conversa sem dono, o backend auto-atribui no envio.
          //  - vendedora em compras: libera (compras nao tem regra).
          //  - vendedora atribuida a ela: libera.
          //  - vendedora atribuida a outra: banner "Atribuida a X".
          //  - vendedora em conversa sem dono: banner "Aguardando atribuicao do sistema".
          //
          // ⚠️ Quem libera de verdade e' o `checarPermissaoConversa` (mensagens.routes); esta tela
          // so decide se DESENHA o compositor. As duas leem a MESMA peca —
          // `equipeRespondeSemSerDona` do `@fiodecobre/shared` — porque a lista escrita a mao nos
          // dois lados ja divergiu uma vez: na [DA-539] o front testava `=== 'atendimento'` e o
          // servidor liberava so `compras`, entao a vendedora via o campo de digitar aberto numa
          // conversa do app que voltava 403 no clique.
          //
          // A caixa `app` entrou na lista em 26/08/2026 ([DA-620]): todo mundo da loja ja via
          // todas as conversas do AppCel (DA-539) e nao podia responder nenhuma que nao fosse sua.
          const ehAdmin = usuario?.perfil === 'admin' || usuario?.perfil === 'gerente';
          if (!ehAdmin && !equipeRespondeSemSerDona(conversa.inbox)) {
            const minha = conversa.usuario_atribuido_id === usuario?.id;
            if (!minha) {
              if (conversa.usuario_atribuido_id == null) {
                return (
                  <div className="border-t bg-amber-50 px-4 py-3 text-sm text-amber-800 flex items-center gap-2">
                    <span className="font-medium">Aguardando atribuicao do sistema.</span>
                    <span className="opacity-80">Voce so pode responder quando o sistema atribuir esta conversa a voce.</span>
                  </div>
                );
              }
              return (
                <div className="border-t bg-amber-50 px-4 py-3 text-sm text-amber-800 flex items-center gap-2">
                  <span className="font-medium">Conversa atribuida a {conversa.usuario_atribuido_login || conversa.usuario_atribuido_nome || 'outra vendedora'}.</span>
                  <span className="opacity-80">Voce nao pode responder agora.</span>
                </div>
              );
            }
          }
          return (
            <Composer
              onEnviarTexto={enviarTexto}
              onEnviarMidia={enviarMidia}
              onEnviarAudio={enviarAudio}
              onEnviarPix={enviarPix}
              responderA={responderA}
              onCancelarResposta={() => setResponderA(null)}
              arquivosArrastados={arquivosArrastados}
              onArquivosConsumidos={() => setArquivosArrastados([])}
              textoInjetado={textoComposer}
              onTextoInjetadoConsumido={() => setTextoComposer(null)}
              conversaId={conversa.id}
              mostrarBotaoProduto={conversa.inbox === 'atendimento'}
              // O nome vai pro titulo da tela de envio: ela ocupa a tela inteira e cobre
              // a conversa, entao sem ele o operador perde de vista PRA QUEM esta mandando
              // — e o que sai dali nao volta.
              nomeContato={[ct.nome, ct.sobrenome].filter(Boolean).join(' ') || ct.push_name || ct.telefone || null}
              // O bloco SOBRE O CLIENTE da tela do fly grava nome/etiqueta; a lista e o cabecalho
              // se atualizam pelo mesmo caminho do painel do contato ([DA-684]).
              contatoId={ct.id}
              onContatoAtualizado={(c) => onAtualizarConversa({ id: conversa.id, contato: { ...ct, ...c } as any })}
              // Na caixa `app` o servidor recusa contato/localizacao/Pix com 409
              // (`bloquearSeCanalSemSuporte`) — la so passam texto, audio, foto e PDF.
              mostrarEnviosDoWhatsApp={conversa.inbox !== 'app'}
            />
          );
        })()}
      </div>

      {mostrarContato && (
        <PainelContato
          contato={ct}
          conversaId={conversa.id}
          onFechar={() => setMostrarContato(false)}
          onAtualizar={(c) => onAtualizarConversa({ id: conversa.id, contato: { ...ct, ...c } as any })}
          onApagar={(id) => { setMostrarContato(false); onApagarContato(id); }}
        />
      )}

      <ModalEncaminhar
        aberto={encaminharMsgs.length > 0}
        mensagens={encaminharMsgs}
        inbox={conversa.inbox}
        onFechar={() => setEncaminharMsgs([])}
      />
      <ModalEditarMensagem
        aberto={!!editar}
        mensagem={editar}
        onFechar={() => setEditar(null)}
        onSalvar={aoEditarSalvar}
      />

    </div>
  );
}
